/**
 * Pure resolvers: (property + override + definition) → rendered value.
 * Shared by the runtime hooks and the editor so the preview matches what the
 * deployed site renders.
 */

import type { Property } from '@vibesites/schema';
import type {
  ContentOverride,
  ContentOverrideType,
  ContentTokenDefinition,
  SlotGroupDefinition,
  SlotGroupOverride,
} from './types';
import { resolveRichText } from './richtext-resolver';

// ============================================================================
// Property Field Path Resolution
// ============================================================================

/**
 * Resolve a dot/bracket-separated path against a property object.
 *
 * @example
 * resolveFieldPath(property, 'property_name')            // → "The Heights"
 * resolveFieldPath(property, 'photo_gallery[0].image')   // → "https://..."
 * resolveFieldPath(property, 'amenities[2].title')       // → "Rooftop Pool"
 *
 * Returns null for missing paths or non-stringable values.
 */
export function resolveFieldPath(property: Property | null, path: string | undefined | null): string | null {
  if (!property || !path) return null;

  // Pattern: collection[index].field or simple_field
  const parts = path.split(/\.|\[|\]/).filter(Boolean);

  let current: unknown = property;
  for (const part of parts) {
    if (current == null) return null;

    if (/^\d+$/.test(part)) {
      if (!Array.isArray(current)) return null;
      current = current[parseInt(part, 10)];
    } else {
      current = (current as Record<string, unknown>)[part];
    }
  }

  return stringifyFieldValue(current);
}

/**
 * Coerce a property field value to a string, or null if not representable.
 * Strings pass through; numbers/booleans stringify; everything else is null.
 */
export function stringifyFieldValue(value: unknown): string | null {
  if (value === null || value === undefined) return null;
  if (typeof value === 'string') return value;
  if (typeof value === 'number' || typeof value === 'boolean') return String(value);
  return null;
}

/**
 * Stored literal for VALUE/GENERATOR, or null if this type reads a property
 * field. Content rows pass `overrideType`; slot-group fields pass `type`.
 */
function overrideLiteral(
  type: ContentOverrideType | null | undefined,
  customValue?: string | null,
  generatedValue?: string | null,
): string | null {
  if (!type) return null;
  switch (type) {
    case 'VALUE':
      return customValue ?? null;
    case 'GENERATOR':
      return generatedValue ?? null;
    case 'FIELD_MAPPING':
      return null;
    default: {
      const _exhaustive: never = type;
      return _exhaustive;
    }
  }
}

// ============================================================================
// Content Token Resolution
// ============================================================================

/**
 * Resolve a content token to its final displayed value.
 *
 * Priority:
 *   1. VALUE override      → customValue
 *   2. GENERATOR override  → generatedValue
 *   3. FIELD_MAPPING override → property[mappedField] (falls back to defaultValue)
 *   4. defaultField        → property[defaultField]  (falls back to defaultValue)
 *   5. defaultValue
 *   6. null (a generator field with no default resolves to nothing — generation
 *      fills it; the linter nudges authors to add a default for design/preview)
 *
 * `definition` should already be the *merged* definition (instance defaults
 * layered over schema defaults); the caller is responsible for that merge.
 */
export function resolveContentTokenValue(
  property: Property | null,
  override: ContentOverride | null | undefined,
  definition: Pick<ContentTokenDefinition, 'defaultField' | 'defaultValue'>,
): string | null {
  const { defaultField, defaultValue } = definition;

  const literal = overrideLiteral(override?.overrideType, override?.customValue, override?.generatedValue);
  if (literal !== null) return literal;

  if (override?.overrideType === 'FIELD_MAPPING' && override.mappedField) {
    return resolveFieldPath(property, override.mappedField) ?? defaultValue ?? null;
  }

  return (defaultField ? resolveFieldPath(property, defaultField) : null) ?? defaultValue ?? null;
}

// ============================================================================
// Slot Group Resolution
// ============================================================================

export type SlotGroupResult = Record<string, string | null>;

/**
 * Resolve every field of a slot group to its final displayed value.
 *
 * Per-field priority:
 *   1. fieldOverrides[f] VALUE        → customValue
 *   2. fieldOverrides[f] GENERATOR    → generatedValue
 *   3. fieldOverrides[f] FIELD_MAPPING → property[mappedField] (falls back to defaultValue)
 *   4. sourceIndex + sourceBinding    → property[collection[index].mapping]
 *   5. fieldDef.defaultField          → property[defaultField]
 *   6. fieldDef.defaultValue
 *
 * Richtext fields are auto-resolved against property data so components don't
 * need to call resolveRichText manually.
 */
export function resolveSlotGroupFields(
  property: Property | null,
  override: SlotGroupOverride | null | undefined,
  definition: SlotGroupDefinition,
): SlotGroupResult {
  const result: SlotGroupResult = {};

  for (const [fieldName, fieldDef] of Object.entries(definition.fields)) {
    const fieldOverride = override?.fieldOverrides?.[fieldName];

    const literal = overrideLiteral(fieldOverride?.type, fieldOverride?.customValue, fieldOverride?.generatedValue);
    if (literal !== null) {
      result[fieldName] = literal;
      continue;
    }

    if (fieldOverride?.type === 'FIELD_MAPPING' && fieldOverride.mappedField) {
      const value = resolveFieldPath(property, fieldOverride.mappedField);
      result[fieldName] = value ?? fieldDef.defaultValue ?? null;
      continue;
    }

    if (override?.sourceIndex != null && definition.sourceBinding) {
      const mapping = definition.sourceBinding.fieldMappings[fieldName];
      if (mapping) {
        const path = `${definition.sourceBinding.collection}[${override.sourceIndex}].${mapping}`;
        const value = resolveFieldPath(property, path);
        result[fieldName] = value ?? fieldDef.defaultValue ?? null;
        continue;
      }
    }

    result[fieldName] =
      (fieldDef.defaultField ? resolveFieldPath(property, fieldDef.defaultField) : null) ??
      fieldDef.defaultValue ??
      null;
  }

  // Auto-resolve richtext so callers don't have to. Guard on `property` —
  // resolveRichText dereferences it for embedded field tokens, and this
  // resolver is reachable with a null property (e.g. collectGeneratorRequests
  // before property data has loaded).
  if (property) {
    for (const [fieldName, fieldDef] of Object.entries(definition.fields)) {
      if (fieldDef.type === 'richtext' && result[fieldName]) {
        result[fieldName] = resolveRichText(result[fieldName]!, property as Record<string, unknown>);
      }
    }
  }

  return result;
}

// ============================================================================
// Ungenerated-generator Detection (editor placeholder gating)
// ============================================================================

